import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import React, { useLayoutEffect, useMemo, useState } from 'react';
import { CustomTypeOptions, useTranslation } from 'react-i18next';
import { styled } from 'styled-components';

import hpacsSolutionImage from '../../assets/hscan-landing/hpacs_solution_new.png';
import { Spacer } from '../../common/components/Spacer';

import { QuestionAnswer, Faq, faqData } from './FaqData';

const SectionContent = styled.div`
  margin: 0 auto; /* center */
  max-width: 1130px;
`;

const Section1 = styled.section`
  background: var(--HPACS_BG_grey, #f5f5f5);

  box-sizing: border-box;
  padding-right: 40px;
  padding-left: 40px;

  /* 0부터 320까지는 32, 320부터 650까지는 64로 증가, 650부터 1130까지는 164로 증가 */
  padding-top: calc(
    max((min(100vw, 1130px) - 650px), 0px) / (1130 - 650) * 100 +
      (max((min(100vw, 650px) - 320px), 0px) / (650 - 320) * 32) + 32px
  );
  /* 0부터 320까지는 30, 320부터 650까지는 190으로 증가, 650부터 1130까지는 290으로 증가 */
  padding-bottom: calc(
    (max((min(100vw, 1130px) - 650px), 0px) / (1130 - 650) * 100) +
      (max((min(100vw, 650px) - 320px), 0px) / (650 - 320) * 160) + 30px
  );

  /* 0부터 1130까지는 20, 1130 이 넘으면 넘은 만큼의 절반을 추가 */
  --pos-right: calc(20px + max(0px, calc((100vw - 1130px) / 2)));
  /* 이미지 최대 크기는 560px 혹은 전체의 60% */
  --bg-max-width: min(560px, 60%);
  background-position-x: calc(100% - var(--pos-right));
  /* '자주 묻는 질문'의 '문'에 그림이 겹치지 않도록 잘 조절해본 결과 */
  background-position-y: calc(
    max(
      50% - 10px,
      max(min(100vw, 500px) - 250px, 0px) / (500 - 250) * 40 + 20px
    )
  );
  background-size: var(--bg-max-width);
  background-repeat: no-repeat;
  background-image: url(${hpacsSolutionImage});
`;

const Section1Text = styled.p`
  margin: 0;
  color: var(--HScan_blue_grey, #00025a);
  /* 0부터 320까지는 22, 320부터 650까지는 65로 증가 */
  font-size: calc((min(100vw, 650px) - 320px) / (650 - 320) * 43 + 22px);
  font-weight: 800;
  line-height: 1.7;
  white-space: pre-line;
`;

const FaqSection = styled.section`
  padding: 0 24px;
  padding-top: ${p => (p.theme.isMobile ? '30px' : '97px')};

  ${p => !p.theme.isMobile && 'background: white;'}
`;
const FaqSectionHeading = styled.h3`
  margin: 0;
  color: var(--HScan_light_blue, #2e5be0);
  font-weight: 700;

  ${p =>
    p.theme.isMobile
      ? `
    font-size: 16px;
    line-height: 15.5px;
    letter-spacing: 0.2px;`
      : `
    text-align: center;
    font-size: 39px;
    line-height: 40px;`}
`;
const FaqList = styled.div`
  margin-top: ${p => (p.theme.isMobile ? '22px' : '64px')};
  background: white;
`;

export const FaqPage = () => {
  const { t, i18n } = useTranslation('translation', {
    keyPrefix: 'features.faq',
  });

  const language = i18n.language as keyof CustomTypeOptions['resources'];

  const faqs: Faq[] = useMemo<Faq[]>(() => faqData[language], [language]);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Section1>
        <SectionContent>
          <Section1Text>{t('faq')}</Section1Text>{' '}
        </SectionContent>
      </Section1>
      {faqs.map((faq, i, { length }) => (
        <FaqSection key={faq.title}>
          <SectionContent>
            <FaqSectionHeading>{faq.title}</FaqSectionHeading>
            <FaqList>
              {faq.qas.map((qa, index) => (
                <FaqListItem key={`${faq.title}-${index}`} qa={qa} />
              ))}
            </FaqList>
          </SectionContent>
          {i === length - 1 && <Spacer height={60} />}
        </FaqSection>
      ))}
    </>
  );
};

const FaqListItemWrapper = styled.div<{ $open: boolean }>`
  border: 1px solid #ebebeb;
  ${p => p.$open && 'background: #f6f6f6;'}
`;
const FaqListItemTitle = styled.div<{ $open: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  column-gap: 30px;

  ${p => p.theme.isMobile && 'min-height: 70px;'}
  box-sizing: border-box;
  padding-top: 18px;
  padding-right: 28px;
  padding-bottom: 18px;
  padding-left: 31px;

  ${p =>
    p.$open &&
    `

    &::before {
      content: "";
      width: 5px;
      height: 100%;
      position: absolute;
      top: 0;
      left: 0;
      background: #2e5be0;
    }
    `}
`;
const FaqListItemTitleText = styled.span<{ $open: boolean }>`
  color: var(--text-color, #252b42);
  font-family: Montserrat;
  font-size: 24px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px; /* 100% */
  letter-spacing: 0.1px;

  ${p =>
    p.$open &&
    `
    color: #2e5be0;
    `}

  ${p =>
    p.theme.isMobile &&
    `
    font-size: 14px;
    `}
`;
const FaqListItemToggleButton = styled.button<{ $open: boolean }>`
  margin-left: auto;
  width: 32px;
  height: 32px;
  margin-top: -16px;
  margin-bottom: -16px;
  border-radius: 32px;

  display: flex;
  align-items: center;
  justify-content: center;

  ${p => (p.$open ? 'border: none;' : 'border: 1px solid #d3d3d3;')}
  ${p => (p.$open ? 'background-color: #2e5be0;' : 'background-color: white;')}
`;
const FaqListItemAnswer = styled.div`
  white-space: pre-line;

  ${p =>
    p.theme.isMobile
      ? `
  padding-top: 0px;
  padding-right: 40px;
  padding-bottom: 22px;
  padding-left: 34px;
  `
      : `
  padding-top: 16px;
  padding-right: 34px;
  padding-bottom: 66px;
  padding-left: 34px;
  `}
`;
const FaqListItemAnswerText = styled.span`
  color: #000;
  font-weight: 400;
  line-height: 26px;
  letter-spacing: 0.2px;
  ${p =>
    p.theme.isMobile
      ? `
  font-size: 14px;
  `
      : `
  font-size: 20px;
  `}
`;

interface FaqListItemProps {
  qa: QuestionAnswer;
}
const FaqListItem = ({ qa }: FaqListItemProps) => {
  const [open, setOpen] = useState(false);

  return (
    <FaqListItemWrapper $open={open}>
      <FaqListItemTitle $open={open} onClick={() => setOpen(prev => !prev)}>
        <FaqListItemTitleText $open={open}>{qa.question}</FaqListItemTitleText>
        <FaqListItemToggleButton $open={open}>
          {open ? (
            <KeyboardArrowUpIcon sx={{ color: 'white' }} />
          ) : (
            <KeyboardArrowRightIcon sx={{ color: '#737373' }} />
          )}
        </FaqListItemToggleButton>
      </FaqListItemTitle>
      {open && (
        <FaqListItemAnswer>
          <FaqListItemAnswerText>{qa.answer}</FaqListItemAnswerText>
        </FaqListItemAnswer>
      )}
    </FaqListItemWrapper>
  );
};
